// Shared number and date formatting for the sales analytics page, so every
// panel shows money, percentages and dates the same way.

const wholeDollars = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const centsDollars = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const compactDollars = new Intl.NumberFormat("en-US", {
  style: "currency", currency: "USD", notation: "compact", minimumFractionDigits: 0, maximumFractionDigits: 1,
});
const counts = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MINUS = "−";

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

/** $1,235 (whole dollars) or $1,234.56 with { cents: true }. */
export function money(value, { cents = false } = {}) {
  if (value == null || value === "") return "—";
  const number = toNumber(value);
  const text = (cents ? centsDollars : wholeDollars).format(Math.abs(number));
  return number < 0 ? `${MINUS}${text}` : text;
}

export function compactMoney(value) {
  if (value == null || value === "") return "—";
  const number = toNumber(value);
  const text = compactDollars.format(Math.abs(number));
  return number < 0 ? `${MINUS}${text}` : text;
}

/** +$1,235 / −$1,235 / $0 */
export function signedMoney(value) {
  const number = toNumber(value);
  const text = wholeDollars.format(Math.abs(number));
  return number > 0 ? `+${text}` : number < 0 ? `${MINUS}${text}` : text;
}

export function count(value) {
  if (value == null || value === "") return "—";
  return counts.format(toNumber(value));
}

/** 12.3% — one decimal everywhere. */
export function pct(value, digits = 1) {
  if (value == null || value === "") return "—";
  return `${toNumber(value).toFixed(digits)}%`;
}

/** +12.3% / −4.0% / 0.0%; null means there was nothing to compare with. */
export function signedPct(value, digits = 1) {
  if (value == null || value === "") return null;
  const number = toNumber(value);
  const text = `${Math.abs(number).toFixed(digits)}%`;
  return number > 0 ? `+${text}` : number < 0 ? `${MINUS}${text}` : text;
}

/** "gain" | "decline" | "flat" for colouring a change. */
export function tone(value) {
  const number = toNumber(value);
  return number > 0 ? "gain" : number < 0 ? "decline" : "flat";
}

function parts(value) {
  const [year, month, day] = String(value).slice(0, 10).split("-").map(Number);
  return { year, month, day };
}

/** "Oct 5" or, with { year: true }, "Oct 5, 2026". Dates are YYYY-MM-DD. */
export function shortDate(value, { year = false } = {}) {
  if (!value) return "";
  const date = parts(value);
  return `${MONTHS[date.month - 1]} ${date.day}${year ? `, ${date.year}` : ""}`;
}

/** "Oct 1 – 20", "Sep 28 – Oct 4", or with years when the range crosses one or isn't this year. */
export function shortRange(start, end, thisYear = new Date().getFullYear()) {
  if (!start || !end) return "";
  const from = parts(start);
  const to = parts(end);
  const withYear = from.year !== to.year || to.year !== thisYear;
  if (start === end) return shortDate(start, { year: withYear });
  if (from.year === to.year && from.month === to.month) {
    return `${MONTHS[from.month - 1]} ${from.day} – ${to.day}${withYear ? `, ${to.year}` : ""}`;
  }
  return `${shortDate(start, { year: from.year !== to.year })} – ${shortDate(end, { year: withYear })}`;
}

/** True when a change is big enough to colour: $5,000 or more, or 15% and $1,000. */
export function isMaterial(amount, percent) {
  const dollars = Math.abs(toNumber(amount));
  if (dollars >= 5000) return true;
  return percent != null && Math.abs(toNumber(percent)) >= 15 && dollars >= 1000;
}

/** Like tone(), but "flat" unless the change is material. */
export function materialTone(amount, percent) {
  return isMaterial(amount, percent) ? tone(amount) : "flat";
}

/** +$1,234 (+5.6%), or +$1,234 (new) when there was nothing before. */
export function changeText(amount, percent) {
  return `${signedMoney(amount)} (${percent == null ? "new" : signedPct(percent)})`;
}

/** "a", "a and b", "a, b and c" */
export function listText(items) {
  const list = items.filter(Boolean);
  if (list.length <= 1) return list[0] || "";
  return `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;
}

/** A row's earlier sales: previous_sales, or worked back from the change percent. */
export function previousSales(row) {
  if (row?.previous_sales != null) return toNumber(row.previous_sales);
  if (row?.sales_change_percent == null) return null;
  const change = toNumber(row.sales_change_percent);
  return change <= -100 ? null : toNumber(row.sales) / (1 + change / 100);
}
